import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { AdminHeader, Modal, ConfirmDelete, EditBtn, AddBtn, Field } from "../../components/admin/AdminUI";
import { Plus, ArrowLeft } from "lucide-react";

const emptyAlbum = { title: "", description: "", cover_image: "", sort_order: 0, status: "published" };
const emptyItem = { album_id: "", type: "image", url: "", caption: "", thumbnail: "", sort_order: 0 };

export default function GalleryAdmin() {
  const [albums, setAlbums] = useState([]);
  const [selected, setSelected] = useState(null);
  const [items, setItems] = useState([]);
  const [openA, setOpenA] = useState(false);
  const [openI, setOpenI] = useState(false);
  const [aForm, setAForm] = useState(emptyAlbum);
  const [aEdit, setAEdit] = useState(null);
  const [iForm, setIForm] = useState(emptyItem);

  const loadA = () => api.get("/admin/gallery/albums").then((r) => setAlbums(r.data));
  const loadI = (aid) => api.get(`/admin/gallery/items?album_id=${aid}`).then((r) => setItems(r.data));
  useEffect(() => { loadA(); }, []);
  useEffect(() => { if (selected) loadI(selected.id); }, [selected]);
  const updA = (k) => (e) => setAForm({ ...aForm, [k]: e.target.type === "number" ? Number(e.target.value) : e.target.value });
  const updI = (k) => (e) => setIForm({ ...iForm, [k]: e.target.type === "number" ? Number(e.target.value) : e.target.value });

  const saveA = async (e) => {
    e.preventDefault();
    try {
      if (aEdit) await api.put(`/admin/gallery/albums/${aEdit}`, aForm);
      else await api.post("/admin/gallery/albums", aForm);
      toast.success(aEdit ? "Updated" : "Created");
      setOpenA(false); setAForm(emptyAlbum); setAEdit(null); loadA();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };
  const editA = (a) => { setAEdit(a.id); setAForm({ ...emptyAlbum, ...a }); setOpenA(true); };
  const delA = async (id) => { await api.delete(`/admin/gallery/albums/${id}`); loadA(); if (selected?.id === id) setSelected(null); toast.success("Deleted"); };

  const saveI = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/gallery/items", { ...iForm, album_id: selected.id });
      toast.success("Added"); setOpenI(false); setIForm(emptyItem); loadI(selected.id);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };
  const delI = async (id) => { await api.delete(`/admin/gallery/items/${id}`); loadI(selected.id); toast.success("Deleted"); };

  if (selected) {
    return (
      <div>
        <AdminHeader title={selected.title} subtitle={`Gallery items · ${items.length}`} actions={<div className="flex gap-2"><button onClick={() => setSelected(null)} className="btn-ghost"><ArrowLeft size={16} /> Albums</button><button onClick={() => { setIForm({ ...emptyItem, album_id: selected.id }); setOpenI(true); }} className="btn-primary"><Plus size={16} /> Add Item</button></div>} />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((it) => (
            <div key={it.id} className="card-elev group relative">
              <img src={it.url} alt={it.caption} className="aspect-square w-full object-cover" />
              <div className="p-3 flex items-center justify-between text-xs">
                <span className="text-brand-muted line-clamp-1">{it.caption}</span>
                <ConfirmDelete onConfirm={() => delI(it.id)} />
              </div>
            </div>
          ))}
        </div>
        <Modal open={openI} onClose={() => setOpenI(false)} title="Add Gallery Item">
          <form onSubmit={saveI} className="grid gap-4">
            <Field label="Type"><select value={iForm.type} onChange={updI("type")} className="form-input"><option value="image">Image</option><option value="video">Video</option></select></Field>
            <Field label="URL *"><input required value={iForm.url} onChange={updI("url")} className="form-input" /></Field>
            <Field label="Thumbnail (optional)"><input value={iForm.thumbnail} onChange={updI("thumbnail")} className="form-input" /></Field>
            <Field label="Caption"><input value={iForm.caption} onChange={updI("caption")} className="form-input" /></Field>
            <Field label="Sort Order"><input type="number" value={iForm.sort_order} onChange={updI("sort_order")} className="form-input" /></Field>
            <div className="flex justify-end gap-2"><button type="button" onClick={() => setOpenI(false)} className="btn-ghost">Cancel</button><button className="btn-primary">Add</button></div>
          </form>
        </Modal>
      </div>
    );
  }

  return (
    <div>
      <AdminHeader title="Gallery Albums" subtitle="Organize photos into albums" actions={<AddBtn onClick={() => { setAEdit(null); setAForm(emptyAlbum); setOpenA(true); }} label="New Album" />} />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {albums.map((a) => (
          <div key={a.id} className="card-elev">
            {a.cover_image && <img src={a.cover_image} alt={a.title} className="aspect-[16/10] w-full object-cover" />}
            <div className="p-5">
              <div className="flex items-center justify-between">
                <div className="font-serif font-bold text-brand-text">{a.title}</div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${a.status === "published" ? "bg-brand-accent/10 text-brand-accent" : "bg-slate-100 text-slate-600"}`}>{a.status}</span>
              </div>
              {a.description && <p className="text-sm text-brand-muted mt-2 line-clamp-2">{a.description}</p>}
              <div className="mt-4 flex items-center justify-between border-t border-brand-border pt-3">
                <button onClick={() => setSelected(a)} className="text-sm font-semibold text-brand-primary hover:text-brand-primary-hover" data-testid={`open-album-${a.id}`}>Open Items →</button>
                <div className="flex items-center gap-1"><EditBtn onClick={() => editA(a)} /><ConfirmDelete onConfirm={() => delA(a.id)} /></div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <Modal open={openA} onClose={() => setOpenA(false)} title={aEdit ? "Edit Album" : "New Album"}>
        <form onSubmit={saveA} className="grid gap-4">
          <Field label="Title *"><input required value={aForm.title} onChange={updA("title")} className="form-input" /></Field>
          <Field label="Cover Image URL"><input value={aForm.cover_image} onChange={updA("cover_image")} className="form-input" /></Field>
          <Field label="Description"><textarea rows={3} value={aForm.description} onChange={updA("description")} className="form-input" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Status"><select value={aForm.status} onChange={updA("status")} className="form-input"><option value="published">Published</option><option value="draft">Draft</option></select></Field>
            <Field label="Sort Order"><input type="number" value={aForm.sort_order} onChange={updA("sort_order")} className="form-input" /></Field>
          </div>
          <div className="flex justify-end gap-2"><button type="button" onClick={() => setOpenA(false)} className="btn-ghost">Cancel</button><button className="btn-primary">{aEdit ? "Update" : "Create"}</button></div>
        </form>
      </Modal>
    </div>
  );
}
